import {
    type ComponentType,
    createContext,
    useContext,
    useEffect,
    type FC
} from "react";
import type {
    CarouselPartContentProps,
    CarouselContextType,
    CarouselPartKind
} from "./type";
import {
    warnOnce
} from "../../utils/development";

export const CAROUSEL_PART_NAMES: Record<CarouselPartKind, string> = {
    indicators: "Indicators",
    previous: "Previous",
    content: "Content",
    pause: "Pause",
    item: "Item",
    next: "Next"
};

export const CarouselContext = createContext<CarouselContextType | undefined>(undefined);

export const CarouselItemContext = createContext<number>(0);

export const useCarouselContext = ({
    part
}: {
    part: CarouselPartKind;
}): CarouselContextType | undefined => {
    const context = useContext(CarouselContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: `Carousel.${CAROUSEL_PART_NAMES[part]}: must be rendered inside <Carousel>.`
        });
    }, [
        context,
        part
    ]);

    return context;
};

export const withCarouselContext = <P extends object>(
    part: CarouselPartKind,
    Content: ComponentType<CarouselPartContentProps<P>>
): FC<P> => {
    const CarouselPartBase: FC<P> = (props) => {
        const context = useCarouselContext({
            part
        });

        if(!context) {
            return null;
        }

        return <Content
            {...props}
            context={context}
        />;
    };

    CarouselPartBase.displayName = `Carousel.${CAROUSEL_PART_NAMES[part]}`;

    return CarouselPartBase;
};
